import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map, switchMap } from 'rxjs';
import { SnackbarService, WindowService } from '@nx-customer-apps/shared/services';
import { FooterComponent } from './core/components/footer/footer.component';
import { ContentComponent } from './core/components/content/content.component';
import { AppService } from './app.service';
import { LanguageSwitcherService } from './core/services';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: true,
    imports: [RouterOutlet, TranslateModule, FooterComponent, ContentComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
    readonly appService = inject(AppService);
    private readonly translateService = inject(TranslateService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly swUpdate = inject(SwUpdate);
    private readonly windowService = inject(WindowService);
    private readonly languageSwitcher = inject(LanguageSwitcherService);

    ngOnInit(): void {
        this.checkForNewVersion();
        this.translateService.use(this.languageSwitcher.getDefaultLocale());
    }

    private checkForNewVersion(): void {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.checkForUpdate();
            this.swUpdate.versionUpdates
                .pipe(
                    filter(versionEvent => versionEvent.type === 'VERSION_READY'),
                    switchMap(() => this.translateService.get(['PTC.NEW_VERSION', 'PTC.BUTTON.RELOAD'])),
                    map(keys => ({
                        message: keys['PTC.NEW_VERSION'],
                        action: keys['PTC.BUTTON.RELOAD']
                    })),
                    switchMap(({ message, action }) =>
                        this.snackbarService
                            .showInfo(message, {
                                duration: 0,
                                action,
                                verticalPosition: 'top',
                                horizontalPosition: 'center'
                            })
                            .onAction()
                    )
                )
                .subscribe(() => {
                    this.windowService.nativeWindow.location.reload();
                });
        }
    }
}
